/**
 * Checkout and subscription API functions
 * Handles payment processing and subscription management
 */

import { AuthResult, AuthResultSuccess, AuthResultError } from '@/app/utils/authentication';
import { PlanTier, TIER_TO_MEMBERSHIP_PACKAGE } from '@/app/config/plans';
import { generateOAuthHeader, getOAuthCredentialsFromSession } from '@/app/utils/oauth';

// Re-export auth result types
export type { AuthResult, AuthResultSuccess, AuthResultError };

// ============ Types ============

export interface CreateSubscriptionParams {
  /** The plan tier being purchased */
  tier: PlanTier;
  /** Subscription length in years (1-5) for yearly plans, 1 for monthly/lifetime */
  subscriptionLength: number;
  /** Payment nonce from Braintree tokenization */
  paymentMethodNonce?: string;
  /** Existing payment method ID (for returning customers) */
  paymentMethodId?: string;
  /** Promo/discount code */
  promoCode?: string;
}

export interface UpgradeSubscriptionParams {
  /** The plan tier to upgrade to */
  tier: PlanTier;
  /** Subscription length in years (1-5) for yearly plans, 1 for monthly/lifetime */
  subscriptionLength: number;
  /** Payment nonce from Braintree tokenization */
  paymentMethodNonce: string;
  /** Promo/discount code */
  promoCode?: string;
}

export interface PromoCodeValidationParams {
  /** The promo code to validate */
  promoCode: string;
  /** The plan tier */
  tier: PlanTier;
  /** Subscription length in years (1-5) */
  subscriptionLength: number;
  /** Optional: Total price to apply discount to */
  price?: number;
}

export interface PromoCodeValidationResult {
  /** Final total after discount */
  total: number;
  /** Discount amount */
  discount: number;
  /** Percentage discount (0-100) */
  percentageDiscount: number;
}

export interface PromoCodeValidationError {
  error: {
    title: string;
    message: string;
  };
}

export type PromoCodeValidationResponse = PromoCodeValidationResult | PromoCodeValidationError;

// ============ API Configuration ============

const API_HOST = process.env.NEXT_PUBLIC_API_HOST || '';

// ============ API Functions ============

/**
 * Create a new subscription for a user
 * Used for first-time Pro/Pro+ purchases
 */
export async function createSubscription(params: CreateSubscriptionParams): Promise<AuthResult> {
  const url = API_HOST + '/api/subscription';
  const membershipPackageName = TIER_TO_MEMBERSHIP_PACKAGE[params.tier];

  const data: Record<string, unknown> = {
    existing: 1,
    client: 'penzu_web',
    membership_package_name: membershipPackageName,
    subscription: params.subscriptionLength,
  };

  if (params.paymentMethodNonce) {
    data.payment_method_nonce = params.paymentMethodNonce;
  }

  if (params.paymentMethodId) {
    data.payment_method_id = params.paymentMethodId;
  }

  if (params.promoCode) {
    data.promo_code = params.promoCode;
  }

  // Get OAuth credentials from session
  const credentials = getOAuthCredentialsFromSession();
  if (!credentials) {
    return {
      error: {
        title: 'Authentication required',
        message: 'Please sign in to continue with your purchase.',
      },
    };
  }

  // Generate OAuth authorization header
  const authHeader = await generateOAuthHeader('POST', url, credentials);

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authHeader,
    },
    redirect: 'follow',
    body: JSON.stringify(data),
  });

  try {
    const result = await response.json();
    if ((result as AuthResultError).error || (result as AuthResultSuccess).access_token) {
      return result;
    }
    throw new Error('Bad response from server: ' + JSON.stringify(result));
  } catch (error) {
    console.error('Unexpected error creating subscription: ', error);
    let message = 'Sorry there was a problem processing your payment. Please try again.';
    if (error instanceof Error) {
      message = error.message;
    }
    return {
      error: {
        title: 'Payment failed',
        message,
      },
    };
  }
}

/**
 * Upgrade an existing subscription
 * Used for Pro -> Pro+ upgrades or extending subscription
 */
export async function upgradeSubscription(params: UpgradeSubscriptionParams): Promise<AuthResult> {
  const url = API_HOST + '/api/subscription';
  const membershipPackageName = TIER_TO_MEMBERSHIP_PACKAGE[params.tier];

  const data: Record<string, unknown> = {
    payment_method_nonce: params.paymentMethodNonce,
    membership_package_name: membershipPackageName,
    subscription: params.subscriptionLength,
    client: 'penzu_web',
  };

  if (params.promoCode) {
    data.promo_code = params.promoCode;
  }

  // Get OAuth credentials from session
  const credentials = getOAuthCredentialsFromSession();
  if (!credentials) {
    return {
      error: {
        title: 'Authentication required',
        message: 'Please sign in to continue with your upgrade.',
      },
    };
  }

  // Generate OAuth authorization header
  const authHeader = await generateOAuthHeader('PUT', url, credentials);

  const response = await fetch(url, {
    method: 'PUT',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: authHeader,
    },
    redirect: 'follow',
    body: JSON.stringify(data),
  });

  try {
    const result = await response.json();
    if ((result as AuthResultError).error || (result as AuthResultSuccess).access_token) {
      return result;
    }
    throw new Error('Bad response from server: ' + JSON.stringify(result));
  } catch (error) {
    console.error('Unexpected error upgrading subscription: ', error);
    let message = 'Sorry there was a problem processing your upgrade. Please try again.';
    if (error instanceof Error) {
      message = error.message;
    }
    return {
      error: {
        title: 'Upgrade failed',
        message,
      },
    };
  }
}

/**
 * Validate a promo code before purchase
 * Returns discount information if valid
 */
export async function validatePromoCode(
  params: PromoCodeValidationParams
): Promise<PromoCodeValidationResponse> {
  const url = API_HOST + '/api/subscription/validate_promo_code';
  const membershipPackageName = TIER_TO_MEMBERSHIP_PACKAGE[params.tier];

  const data: Record<string, unknown> = {
    promo_code: params.promoCode,
    membership_package_name: membershipPackageName,
    subscription: params.subscriptionLength,
  };

  if (params.price !== undefined) {
    data.price = params.price.toString();
  }

  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    redirect: 'follow',
    body: JSON.stringify(data),
  });

  try {
    const result = await response.json();
    // Check if it's an error response
    if ((result as PromoCodeValidationError).error) {
      return result as PromoCodeValidationError;
    }
    // Otherwise assume it's a success response
    return {
      total: result.total,
      discount: result.discount,
      percentageDiscount: result.percentage_discount,
    };
  } catch (error) {
    console.error('Unexpected error validating promo code: ', error);
    return {
      error: {
        title: 'Validation failed',
        message: 'Unable to validate promo code. Please try again.',
      },
    };
  }
}

// ============ Type Guards ============

/**
 * Check if a promo code validation result is successful
 */
export function isPromoCodeValid(
  result: PromoCodeValidationResponse
): result is PromoCodeValidationResult {
  return !('error' in result);
}

/**
 * Check if a promo code validation result is an error
 */
export function isPromoCodeError(
  result: PromoCodeValidationResponse
): result is PromoCodeValidationError {
  return 'error' in result;
}
